import { z } from 'zod';
import { ProtocolType, VehicleCategory } from './enums';

export const PhotoKind = {
  FRONT: 'FRONT',
  HECK: 'HECK',
  SEITE_LINKS: 'SEITE_LINKS',
  SEITE_RECHTS: 'SEITE_RECHTS',
  KOMBIINSTRUMENT: 'KOMBIINSTRUMENT',
  INNENRAUM_VORNE: 'INNENRAUM_VORNE',
  INNENRAUM_HINTEN: 'INNENRAUM_HINTEN',
  LADERAUM: 'LADERAUM',
  DACH: 'DACH',
  REIFEN: 'REIFEN',
  SCHADEN: 'SCHADEN',
  SONSTIGES: 'SONSTIGES',
} as const;
export type PhotoKind = (typeof PhotoKind)[keyof typeof PhotoKind];
export const photoKindSchema = z.nativeEnum(PhotoKind);

export const PHOTO_KIND_LABEL: Record<PhotoKind, string> = {
  FRONT: 'Front',
  HECK: 'Heck',
  SEITE_LINKS: 'Seite links',
  SEITE_RECHTS: 'Seite rechts',
  KOMBIINSTRUMENT: 'Kombiinstrument (km-Stand)',
  INNENRAUM_VORNE: 'Innenraum vorne',
  INNENRAUM_HINTEN: 'Innenraum hinten',
  LADERAUM: 'Laderaum',
  DACH: 'Dach',
  REIFEN: 'Reifen / Felgen',
  SCHADEN: 'Schaden (Detail)',
  SONSTIGES: 'Sonstiges',
};

export interface PhotoSlot {
  kind: PhotoKind;
  label: string;
  required: boolean;
  hint?: string;
}

const slot = (kind: PhotoKind, required: boolean, hint?: string): PhotoSlot => ({
  kind,
  label: PHOTO_KIND_LABEL[kind],
  required,
  ...(hint ? { hint } : {}),
});

/**
 * Pflicht-Fotoserie je Vorgangsart.
 * Uebergabe/Rueckgabe = volle Dokumentation (beweissicher fuer Leasingrueckgabe),
 * Pool = bewusst auf 5 schnelle Aufnahmen reduziert, Check-In = 4 Rundum + Tacho.
 */
export function photoSeriesFor(type: ProtocolType, category: VehicleCategory): PhotoSlot[] {
  const rundum = [
    slot(PhotoKind.FRONT, true, 'Schrägansicht vorne links, ganzes Fahrzeug im Bild'),
    slot(PhotoKind.HECK, true, 'Schrägansicht hinten rechts, Kennzeichen lesbar'),
    slot(PhotoKind.SEITE_LINKS, true),
    slot(PhotoKind.SEITE_RECHTS, true),
  ];
  const tacho = slot(PhotoKind.KOMBIINSTRUMENT, true, 'Zündung an, km-Stand deutlich lesbar');

  switch (type) {
    case ProtocolType.UEBERGABE:
    case ProtocolType.RUECKGABE:
      return [
        ...rundum,
        tacho,
        slot(PhotoKind.INNENRAUM_VORNE, true),
        slot(PhotoKind.INNENRAUM_HINTEN, true),
        ...(category === VehicleCategory.LCV ? [slot(PhotoKind.LADERAUM, true)] : []),
        slot(PhotoKind.DACH, false),
        slot(PhotoKind.REIFEN, false, 'Bei auffälligem Profil oder Felgenschaden'),
      ];
    case ProtocolType.POOL_AUSGABE:
    case ProtocolType.POOL_RUECKNAHME:
      return [...rundum, tacho];
    case ProtocolType.ZUSTANDSCHECK:
      return [
        ...rundum,
        tacho,
        slot(PhotoKind.INNENRAUM_VORNE, false),
        ...(category === VehicleCategory.LCV ? [slot(PhotoKind.LADERAUM, false)] : []),
      ];
    case ProtocolType.NUTZERWECHSEL:
    default:
      return [];
  }
}

export const photoMetaSchema = z.object({
  clientId: z.string().min(1).max(64),
  kind: photoKindSchema,
  note: z.string().max(500).optional(),
  takenAt: z.string().datetime().optional(),
  lat: z.number().min(-90).max(90).optional().nullable(),
  lng: z.number().min(-180).max(180).optional().nullable(),
});
export type PhotoMetaInput = z.infer<typeof photoMetaSchema>;
